import { useEffect, useState } from "react";
import { getDataSources, type DataSource } from "@/src/services/data_sources_service";
import type { Result } from "@/src/services/types";
import { dataSourceToItem, type DataSourceItem } from "./catalog";

export type DataSourcesStatus = "loading" | "live" | "empty";

/**
 * Pure resolver: turn a `getDataSources()` Result into the picker's items +
 * status. No fallback catalog — an error or an empty `/sources/` response
 * just resolves to an empty item list. Extracted from the hook so it is
 * unit-testable without rendering.
 */
export function resolveDataSourceItems(res: Result<DataSource[]>): {
  items: DataSourceItem[];
  status: Exclude<DataSourcesStatus, "loading">;
} {
  const items = res.ok ? res.value.map(dataSourceToItem) : [];
  return { items, status: items.length > 0 ? "live" : "empty" };
}

/** Provide the data-source (watchlist) catalog to the picker from `GET /sources/`. */
export function useDataSources(): { items: DataSourceItem[]; status: DataSourcesStatus } {
  const [items, setItems] = useState<DataSourceItem[]>([]);
  const [status, setStatus] = useState<DataSourcesStatus>("loading");

  useEffect(() => {
    let active = true;
    void getDataSources().then((res) => {
      if (!active) return;
      const resolved = resolveDataSourceItems(res);
      setItems(resolved.items);
      setStatus(resolved.status);
    });
    return () => {
      active = false;
    };
  }, []);

  return { items, status };
}
